import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "./AuthProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, User, Command, Menu } from "lucide-react";
import { CommandPalette } from "./command-palette/CommandPalette";

const navigation = [
  { name: "Analytics", href: "/dashboard", tab: "dashboard" },
  { name: "Exploitation", href: "/clients-architecture", tab: "clients" },
  { name: "Initiatives", href: "/initiatives", tab: "initiatives" },
  { name: "Activities", href: "/activities", tab: "activities" },
  { name: "Proposals", href: "/proposals", tab: "proposals" },
  { name: "Projects", href: "/projects", tab: "projects" },
  { name: "Wisdrop", href: "/wisdrop", tab: "wisdrop" },
  { name: "Clients", href: "/clients", tab: "clients" },
  { name: "People", href: "/people", tab: "people" },
  { name: "Xero-Lite", href: "/xero-lite/invoices", tab: "xero-lite" },
  { name: "Users", href: "/users", tab: "users" },
  { name: "Settings", href: "/settings", tab: "settings" },
];

export default function Layout() {
  const { user, loading, signOut, userProfile, userRole: authUserRole } = useAuth();
  const { hasPermission, loading: permissionsLoading, permissions, userRole } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Debug current user
  console.log("Current user:", user?.email, "User role:", authUserRole, "Permissions:", permissions);
  console.log("User profile:", userProfile);
  console.log("Auth loading:", loading, "Permissions loading:", permissionsLoading);
  const [commandOpen, setCommandOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  // Add keyboard shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 flex items-center justify-center mx-auto mb-4">
            <img 
              src="/lovable-uploads/d11ad2b7-53d8-4cca-ab99-643fb1a5896e.png" 
              alt="Company Logo" 
              className="w-8 h-8 object-contain"
            />
          </div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to auth
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Mobile Menu */}
            <div className="flex items-center space-x-3">
              {/* Mobile Menu Button - Only visible on mobile */}
              {isMobile && (
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="sm" className="md:hidden" aria-label="Open menu">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80">
                    <div className="flex flex-col space-y-4 mt-8">
                      {!permissionsLoading && navigation
                        .filter(item => {
                          // Check if user has permission to view this tab
                          if (user && hasPermission(item.tab, 'view')) {
                            return true;
                          }
                          return false;
                        })
                        .map((item) => (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            end={item.href === "/dashboard"}
                            onClick={() => setMobileMenuOpen(false)}
                            className={({ isActive }) => {
                              // Special handling for Xero-Lite to be active for all sub-routes
                              const isXeroLiteActive = item.name === "Xero-Lite" && 
                                location.pathname.startsWith("/xero-lite");
                              const active = isActive || isXeroLiteActive;
                              
                              return cn(
                                "block py-3 px-4 text-base font-medium transition-colors hover:text-primary hover:bg-accent rounded-md",
                                active
                                  ? "text-primary bg-accent"
                                  : "text-muted-foreground"
                              );
                            }}
                            aria-label={`Navigate to ${item.name}`}
                          >
                            {item.name}
                          </NavLink>
                        ))}
                    </div>
                  </SheetContent>
                </Sheet>
              )}
              
              {/* Logo */}
              <div className="w-8 h-8 flex items-center justify-center">
                <img 
                  src="/lovable-uploads/d11ad2b7-53d8-4cca-ab99-643fb1a5896e.png" 
                  alt="Company Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Braincandy</h1>
                <p className="text-sm text-muted-foreground">NEXUS</p>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                 <Avatar className="h-8 w-8">
                   {userProfile?.profile_image_url && (
                     <AvatarImage 
                       src={userProfile.profile_image_url} 
                       alt="User avatar"
                     />
                   )}
                   <AvatarFallback>
                     {userProfile?.first_name && userProfile?.last_name 
                       ? `${userProfile.first_name.charAt(0)}${userProfile.last_name.charAt(0)}`
                       : userProfile?.full_name?.charAt(0)?.toUpperCase() ||
                         (user?.email ? user.email.charAt(0).toUpperCase() : 'U')}
                   </AvatarFallback>
                 </Avatar>
                <span className="text-sm font-medium">
                  {userProfile?.first_name && userProfile?.last_name 
                    ? `${userProfile.first_name} ${userProfile.last_name.charAt(0)}.`
                    : userProfile?.full_name || userProfile?.first_name || 
                      (user?.email ? user.email.split('@')[0] : user?.email)}
                </span>
              </div>
            <Button variant="ghost" size="sm" onClick={signOut} aria-label="Sign out">
              <LogOut className="h-4 w-4" />
            </Button>
            </div>
          </div>
        </div>

        {/* Desktop Navigation - Hidden on mobile */}
        <nav className="border-t border-border hidden md:block">
          <div className="px-6">
            <div className="flex space-x-8">
              {!permissionsLoading && navigation
                .filter(item => {
                  // Check if user has permission to view this tab
                  if (user && hasPermission(item.tab, 'view')) {
                    console.log(`Tab ${item.tab}: allowing access for user with role ${authUserRole}`);
                    return true;
                  }
                  console.log(`Tab ${item.tab}: denying access for user with role ${authUserRole}`);
                  return false;
                })
                .map((item) => (
                   <NavLink
                     key={item.name}
                     to={item.href}
                     end={item.href === "/dashboard"}
                     className={({ isActive }) => {
                       // Special handling for Xero-Lite to be active for all sub-routes
                       const isXeroLiteActive = item.name === "Xero-Lite" && 
                         location.pathname.startsWith("/xero-lite");
                       const active = isActive || isXeroLiteActive;
                       
                       return cn(
                         "relative py-4 text-sm font-medium transition-colors hover:text-primary",
                         active
                           ? "text-primary border-b-2 border-primary"
                           : "text-muted-foreground"
                       );
                     }}
                     aria-label={`Navigate to ${item.name}`}
                   >
                     {item.name}
                   </NavLink>
               ))}
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>
      
      {/* Command Palette */}
      <CommandPalette open={commandOpen} setOpen={setCommandOpen} />
    </div>
  );
}